import { useEffect, useMemo, useState } from 'react';
import { Play, Clock } from 'lucide-react';
import api from '@/lib/api';
import { usePlayer } from '@/contexts/PlayerContext';
import DownloadButton from '@/components/DownloadButton';

export default function Library() {
    const { playTrack, setFullscreen } = usePlayer();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [maxMin, setMaxMin] = useState(0);

    useEffect(() => {
        api.get('/meditations').then((r) => setItems(r.data || [])).catch(() => {});
    }, []);

    const categories = useMemo(() => {
        const c = new Set(items.map((i) => i.category));
        return ['all', ...Array.from(c)];
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter(
            (i) => (filter === 'all' || i.category === filter) && (maxMin === 0 || i.duration_minutes <= maxMin)
        );
    }, [items, filter, maxMin]);

    const start = (m) => {
        playTrack({
            id: m.id, title: m.title, subtitle: m.instructor,
            image: m.cover_image, audio_url: m.audio_url,
            kind: 'meditation', duration_minutes: m.duration_minutes,
        });
        setFullscreen(true);
    };

    return (
        <div className="space-y-8" data-testid="library-page">
            <header>
                <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Curated library</p>
                <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">Find your session</h1>
                <p className="text-forest-soft mt-2 max-w-xl">
                    A small, hand-picked collection. Choose by feeling — not by feature.
                </p>
            </header>

            <div className="qm-card p-4 md:p-5 flex flex-wrap items-center gap-2" data-testid="library-filters">
                {categories.map((c) => (
                    <button
                        key={c}
                        onClick={() => setFilter(c)}
                        data-testid={`filter-${c}`}
                        className={`qm-pill ${filter === c ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}
                    >
                        {c === 'all' ? 'All' : c}
                    </button>
                ))}
                <span className="ml-auto text-xs text-forest-soft hidden sm:inline">Max minutes</span>
                <div className="flex items-center gap-1">
                    {[0, 5, 10, 15].map((m) => (
                        <button
                            key={m}
                            onClick={() => setMaxMin(m)}
                            data-testid={`maxmin-${m}`}
                            className={`qm-pill text-xs ${maxMin === m ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}
                        >
                            {m === 0 ? 'Any' : `≤ ${m}`}
                        </button>
                    ))}
                </div>
            </div>

            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="library-grid">
                {filtered.map((m, idx) => (
                    <li key={m.id} className="qm-card overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all" style={{ animationDelay: `${idx * 30}ms` }}>
                        <div
                            className="aspect-[5/3] relative"
                            style={{
                                backgroundImage: `linear-gradient(180deg, transparent, rgba(45,74,62,0.3)), url(${m.cover_image})`,
                                backgroundSize: 'cover', backgroundPosition: 'center',
                            }}
                        >
                            <button
                                onClick={() => start(m)}
                                data-testid={`play-${m.id}`}
                                aria-label={`Play ${m.title}`}
                                className="absolute bottom-3 right-3 rounded-full w-12 h-12 grid place-items-center bg-cream text-forest hover:scale-110 active:scale-95 transition shadow-md"
                            >
                                <Play size={20} className="ml-0.5" />
                            </button>
                        </div>
                        <div className="p-5">
                            <div className="flex items-center gap-2 text-xs text-forest-soft">
                                <span className="qm-pill bg-sage/30 border-transparent text-xs px-3 py-1">{m.category}</span>
                                <span className="inline-flex items-center gap-1"><Clock size={12} /> {m.duration_minutes}m</span>
                                <DownloadButton
                                    item={{ id: m.id, title: m.title, audio_url: m.audio_url, category: m.category, kind: 'meditation', cover_image: m.cover_image, duration_minutes: m.duration_minutes }}
                                    className="ml-auto !w-7 !h-7"
                                    testIdPrefix="dl-med"
                                />
                            </div>
                            <h3 className="font-serif text-xl text-forest mt-3">{m.title}</h3>
                            <p className="text-sm text-forest-soft mt-1 line-clamp-2">{m.description}</p>
                            {m.instructor && <p className="text-xs text-forest-soft/70 mt-3">with {m.instructor}</p>}
                        </div>
                    </li>
                ))}
            </ul>

            {filtered.length === 0 && (
                <div className="qm-card p-10 text-center text-forest-soft" data-testid="library-empty">
                    Nothing matches that combination yet — try a different filter.
                </div>
            )}
        </div>
    );
}
